import { hoyLocal, fechaLocal, mesLocal } from '@/lib/fechas';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Plus, Trash2, DollarSign, UserCog, ShieldCheck, Wrench, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PUESTO_LABELS = {
  gerente: 'Gerente', cajero: 'Cajero', operario: 'Operario',
  lavador: 'Lavador', encargado: 'Encargado', supervisor: 'Supervisor',
  detallador: 'Detallador', recepcionista: 'Recepcionista', otro: 'Otro',
};

export default function CarwashNomina() {
  const [tab, setTab] = useState('personal');
  const [staff, setStaff] = useState([]);
  const [nomina, setNomina] = useState([]);
  const [faltas, setFaltas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [faltaDialog, setFaltaDialog] = useState(false);
  const [faltaForm, setFaltaForm] = useState({
    empleado_id: '', fecha: hoyLocal(), motivo: '', descuento_monto: '',
  });

  // Pago dialog
  const [nomDialog, setNomDialog] = useState(false);
  const [nomForm, setNomForm] = useState({
    empleado_id: '',
    fecha: hoyLocal(),
    concepto: 'Pago semanal',
    monto: '',
    desde: '',
    hasta: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [sugerencia, setSugerencia] = useState(null); // { monto_sugerido, detalle, turnos_count, ... }
  const [calculandoSug, setCalculandoSug] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const [sRes, nRes, fRes] = await Promise.all([
        api.get('/carwash/staff'),
        api.get('/carwash/nomina'),
        api.get('/carwash/faltas'),
      ]);
      setStaff(sRes.data);
      setNomina(nRes.data);
      setFaltas(fRes.data || []);
    } catch { /* silent */ }
    finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ─── Faltas ───
  const abrirNuevaFalta = (empleado = null) => {
    setFaltaForm({
      empleado_id: empleado?.id || '',
      fecha: hoyLocal(),
      motivo: '',
      descuento_monto: '',  // vacio = auto-calcula sueldo diario
    });
    setFaltaDialog(true);
  };

  const empleadoFaltaSel = staff.find(s => s.id === faltaForm.empleado_id);
  const sueldoDiarioSugerido = (() => {
    if (!empleadoFaltaSel) return 0;
    const dias = empleadoFaltaSel.frecuencia_pago === 'diario' ? 1
               : empleadoFaltaSel.frecuencia_pago === 'quincenal' ? 15 : 7;
    return (empleadoFaltaSel.salario_base || 0) / dias;
  })();

  const guardarFalta = async () => {
    if (!faltaForm.empleado_id) { toast.error('Selecciona un empleado'); return; }
    if (!faltaForm.fecha) { toast.error('Fecha obligatoria'); return; }
    try {
      const payload = {
        empleado_id: faltaForm.empleado_id,
        fecha: faltaForm.fecha,
        motivo: faltaForm.motivo,
      };
      if (faltaForm.descuento_monto !== '' && faltaForm.descuento_monto != null) {
        payload.descuento_monto = parseFloat(faltaForm.descuento_monto);
      }
      await api.post('/carwash/faltas', payload);
      toast.success('Falta registrada');
      setFaltaDialog(false);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar falta'); }
  };

  const eliminarFalta = async (id) => {
    if (!window.confirm('Eliminar esta falta?')) return;
    try {
      await api.delete(`/carwash/faltas/${id}`);
      toast.success('Falta eliminada');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const abrirNuevoPago = (empleado = null) => {
    // Rango por defecto segun frecuencia_pago del empleado
    const frec = empleado?.frecuencia_pago || 'semanal';
    const dias = frec === 'diario' ? 0 : frec === 'quincenal' ? 14 : 6;
    const hoy = new Date();
    const desde = new Date(hoy);
    desde.setDate(hoy.getDate() - dias);
    setSugerencia(null);
    const conceptoTxt = frec === 'diario' ? 'Pago diario' : frec === 'quincenal' ? 'Pago quincenal' : 'Pago semanal';
    setNomForm({
      empleado_id: empleado?.id || '',
      fecha: fechaLocal(hoy),
      concepto: empleado ? `${conceptoTxt} - ${empleado.nombre}` : conceptoTxt,
      monto: empleado?.salario_base ? String(empleado.salario_base) : '',
      desde: fechaLocal(desde),
      hasta: fechaLocal(hoy),
    });
    setNomDialog(true);
  };

  const calcularSugerido = async () => {
    if (!nomForm.empleado_id) { toast.error('Selecciona un miembro del staff'); return; }
    if (!nomForm.desde || !nomForm.hasta) { toast.error('Define el rango de fechas'); return; }
    setCalculandoSug(true);
    try {
      const r = await api.get(
        `/carwash/nomina/calcular-comision/${nomForm.empleado_id}?desde=${nomForm.desde}&hasta=${nomForm.hasta}`
      );
      setSugerencia(r.data);
      setNomForm(f => ({ ...f, monto: String(r.data.monto_sugerido || 0) }));
      toast.success('Monto sugerido calculado');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al calcular'); }
    finally { setCalculandoSug(false); }
  };

  const guardarPago = async () => {
    if (!nomForm.empleado_id) { toast.error('Selecciona un miembro del staff'); return; }
    if (!nomForm.monto || parseFloat(nomForm.monto) <= 0) { toast.error('Monto invalido'); return; }
    const empleado = staff.find(s => s.id === nomForm.empleado_id);
    setGuardando(true);
    try {
      await api.post('/carwash/nomina', {
        ...nomForm,
        empleado_nombre: empleado?.nombre || '',
      });
      toast.success('Pago de nomina registrado y agregado a contabilidad');
      setNomDialog(false);
      cargar();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar pago'); }
    finally { setGuardando(false); }
  };

  const eliminarPago = async (id) => {
    if (!window.confirm('Eliminar este pago de nomina?')) return;
    try {
      await api.delete(`/carwash/nomina/${id}`);
      toast.success('Pago eliminado');
      cargar();
    } catch { toast.error('Error al eliminar'); }
  };

  const staffActivo = staff.filter(s => s.activo !== false);
  const totalNomina = nomina.reduce((s, n) => s + (n.monto || 0), 0);
  const totalNominaMes = nomina
    .filter(n => n.fecha?.slice(0, 7) === mesLocal())
    .reduce((s, n) => s + (n.monto || 0), 0);

  // Pagos por empleado (para mostrar en lista)
  const pagosPorEmp = nomina.reduce((acc, n) => {
    if (!n.empleado_id) return acc;
    if (!acc[n.empleado_id]) acc[n.empleado_id] = { count: 0, total: 0, ultimo: '' };
    acc[n.empleado_id].count += 1;
    acc[n.empleado_id].total += (n.monto || 0);
    if (!acc[n.empleado_id].ultimo || n.fecha > acc[n.empleado_id].ultimo) {
      acc[n.empleado_id].ultimo = n.fecha;
    }
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="carwash-nomina-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Users className="w-8 h-8 text-cyan-400" /> Nomina
        </h1>
        <div className="flex gap-2">
          {[
            { k: 'personal', label: 'Personal y Sueldos' },
            { k: 'faltas', label: 'Faltas / Descuentos' },
            { k: 'pagos', label: 'Pagos Registrados' }
          ].map(t => (
            <Button key={t.k} variant={tab === t.k ? 'default' : 'ghost'} size="sm"
              onClick={() => setTab(t.k)}
              className={tab === t.k ? 'bg-cyan-500 text-black font-bold' : 'text-slate-400'}
              data-testid={`tab-${t.k}`}>
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Personal Activo</p>
            <p className="text-2xl font-bold text-slate-100 font-mono">{staffActivo.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Pagado este Mes</p>
            <p className="text-2xl font-bold text-cyan-400 font-mono">{formatoMXN(totalNominaMes)}</p>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800">
          <CardContent className="p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Total Historico</p>
            <p className="text-2xl font-bold text-emerald-400 font-mono">{formatoMXN(totalNomina)}</p>
          </CardContent>
        </Card>
      </div>

      {/* ─── TAB: PERSONAL Y SUELDOS ─── */}
      {tab === 'personal' && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="font-barlow text-lg text-slate-200">
                Personal del Negocio ({staffActivo.length})
              </CardTitle>
              <div className="flex gap-2">
                <Link to="/carwash/staff">
                  <Button variant="outline" size="sm" className="border-slate-700 text-slate-300 hover:bg-slate-800"
                    data-testid="ir-a-staff-btn">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Gestionar Staff
                  </Button>
                </Link>
                <Button onClick={() => abrirNuevoPago()} disabled={staffActivo.length === 0}
                  className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm"
                  data-testid="nuevo-pago-nomina-btn">
                  <Plus className="w-4 h-4 mr-2" /> Registrar Pago
                </Button>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Para dar de alta o modificar empleados, ve a la seccion <span className="text-cyan-400">Staff</span>.
              Los pagos registrados aqui se agregan automaticamente a Contabilidad como gasto.
            </p>
          </CardHeader>
          <CardContent>
            {cargando ? (
              <div className="text-center py-8 text-slate-500">Cargando...</div>
            ) : staffActivo.length === 0 ? (
              <div className="text-center py-12">
                <UserCog className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">No hay personal registrado</p>
                <Link to="/carwash/staff">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-sm mt-3">
                    Ir a Staff
                  </Button>
                </Link>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Nombre</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Puesto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Contacto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Sueldo Base</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Pagos / Total</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Accion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staffActivo.map(s => {
                    const pp = pagosPorEmp[s.id] || { count: 0, total: 0, ultimo: '' };
                    return (
                      <TableRow key={s.id} className="border-slate-800/50" data-testid={`personal-row-${s.id}`}>
                        <TableCell className="text-sm text-slate-200 font-medium">
                          <div className="flex items-center gap-2">
                            {s.tiene_acceso
                              ? <ShieldCheck className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                              : <Wrench className="w-3.5 h-3.5 text-slate-500 shrink-0" />}
                            {s.nombre}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-slate-800/60 text-slate-300 border-slate-700 text-[10px]">
                            {PUESTO_LABELS[s.puesto] || s.puesto}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-slate-400">
                          {s.email || s.telefono || '-'}
                        </TableCell>
                        <TableCell className="text-right text-sm text-cyan-400 font-mono">
                          {formatoMXN(s.salario_base || 0)}
                        </TableCell>
                        <TableCell className="text-right text-xs text-slate-400">
                          {pp.count > 0 ? (
                            <div>
                              <div>{pp.count} pago(s)</div>
                              <div className="text-emerald-400 font-mono">{formatoMXN(pp.total)}</div>
                            </div>
                          ) : (
                            <span className="text-slate-600">Sin pagos</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => abrirNuevoPago(s)}
                            className="text-cyan-400 hover:bg-cyan-500/10 text-xs"
                            data-testid={`pagar-${s.id}`}>
                            <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── TAB: FALTAS / DESCUENTOS ─── */}
      {tab === 'faltas' && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-barlow text-lg text-slate-200">
                Faltas Registradas ({faltas.length})
              </CardTitle>
              <p className="text-xs text-slate-500">Las faltas descuentan automáticamente del pago de nómina del periodo.</p>
            </div>
            <Button onClick={() => abrirNuevaFalta()} className="bg-amber-500 text-black font-bold hover:bg-amber-400"
              data-testid="btn-nueva-falta">
              <Plus className="w-4 h-4 mr-1" /> Registrar Falta
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {faltas.length === 0 ? (
              <div className="p-12 text-center">
                <UserCog className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">Sin faltas registradas</p>
                <p className="text-xs text-slate-600 mt-1">Todo el equipo asistió, muy bien!</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Empleado</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Motivo</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Descuento</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faltas.map(f => (
                    <TableRow key={f.id} className="border-slate-800/50" data-testid={`falta-row-${f.id}`}>
                      <TableCell className="text-sm text-slate-400 font-mono">{f.fecha}</TableCell>
                      <TableCell className="text-sm text-slate-200 font-medium">{f.empleado_nombre || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-400">{f.motivo || <span className="text-slate-600">Sin motivo</span>}</TableCell>
                      <TableCell className="text-right text-sm text-red-400 font-mono font-bold">-{formatoMXN(f.descuento_monto)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => eliminarFalta(f.id)}
                          className="text-slate-500 hover:text-red-400 h-7 w-7" data-testid={`eliminar-falta-${f.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── Dialog: Registrar Falta ─── */}
      <Dialog open={faltaDialog} onOpenChange={setFaltaDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="dialog-nueva-falta">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">Registrar Falta</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Empleado</Label>
              <Select value={faltaForm.empleado_id}
                onValueChange={v => setFaltaForm(f => ({ ...f, empleado_id: v, descuento_monto: '' }))}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                  data-testid="falta-empleado-select">
                  <SelectValue placeholder="Selecciona un empleado" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {staff.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-slate-200">{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Fecha de la falta</Label>
              <Input type="date" value={faltaForm.fecha}
                onChange={e => setFaltaForm(f => ({ ...f, fecha: e.target.value }))}
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                data-testid="falta-fecha-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Motivo (opcional)</Label>
              <Input value={faltaForm.motivo}
                onChange={e => setFaltaForm(f => ({ ...f, motivo: e.target.value }))}
                placeholder="Enfermedad, personal, etc."
                className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                data-testid="falta-motivo-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Monto a descontar (MXN)</Label>
              <Input type="number" min="0" step="1" value={faltaForm.descuento_monto}
                onChange={e => setFaltaForm(f => ({ ...f, descuento_monto: e.target.value }))}
                placeholder={empleadoFaltaSel ? `Auto: ${formatoMXN(sueldoDiarioSugerido)}` : 'Sueldo diario prorrateado'}
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono mt-1"
                data-testid="falta-monto-input" />
              {empleadoFaltaSel && (
                <p className="text-[10px] text-cyan-400/80 mt-1">
                  Sugerido: {formatoMXN(sueldoDiarioSugerido)}/día
                  (sueldo {empleadoFaltaSel.frecuencia_pago} {formatoMXN(empleadoFaltaSel.salario_base)} ÷ {empleadoFaltaSel.frecuencia_pago === 'diario' ? 1 : empleadoFaltaSel.frecuencia_pago === 'quincenal' ? 15 : 7} días).
                  Deja vacío para usar este monto.
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFaltaDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardarFalta} className="bg-amber-500 text-black font-bold hover:bg-amber-400"
              data-testid="btn-guardar-falta">
              Registrar Falta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── TAB: PAGOS ─── */}
      {tab === 'pagos' && (
        <Card className="bg-slate-900/40 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="font-barlow text-lg text-slate-200">
              Pagos de Nomina ({nomina.length})
            </CardTitle>
            <p className="text-xs text-slate-500">Cada pago genera automaticamente un gasto en Contabilidad.</p>
          </CardHeader>
          <CardContent className="p-0">
            {nomina.length === 0 ? (
              <div className="p-12 text-center">
                <DollarSign className="w-12 h-12 text-slate-700 mx-auto mb-3" />
                <p className="text-slate-500">Sin pagos registrados</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Empleado</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Concepto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Monto</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nomina.map(n => (
                    <TableRow key={n.id} className="border-slate-800/50" data-testid={`nomina-row-${n.id}`}>
                      <TableCell className="text-sm text-slate-400">{n.fecha}</TableCell>
                      <TableCell className="text-sm text-slate-200 font-medium">{n.empleado_nombre || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-400">{n.concepto}</TableCell>
                      <TableCell className="text-right text-sm text-cyan-400 font-mono font-bold">{formatoMXN(n.monto)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => eliminarPago(n.id)}
                          className="text-slate-500 hover:text-red-400 h-7 w-7" data-testid={`eliminar-pago-${n.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog: Pago Nomina */}
      <Dialog open={nomDialog} onOpenChange={setNomDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="nomina-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl text-slate-100">Registrar Pago de Nomina</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Empleado *</Label>
              <Select value={nomForm.empleado_id} onValueChange={v => {
                const emp = staff.find(s => s.id === v);
                setNomForm({
                  ...nomForm,
                  empleado_id: v,
                  monto: emp?.salario_base ? String(emp.salario_base) : nomForm.monto,
                  concepto: emp ? `Pago semanal - ${emp.nombre}` : nomForm.concepto,
                });
              }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="nom-empleado-select">
                  <SelectValue placeholder="Seleccionar miembro del staff" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 max-h-72">
                  {staffActivo.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-slate-300">
                      {s.nombre} — {PUESTO_LABELS[s.puesto] || s.puesto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Fecha</Label>
                <Input type="date" value={nomForm.fecha}
                  onChange={e => setNomForm({ ...nomForm, fecha: e.target.value })}
                  className="bg-slate-950 border-slate-700 text-slate-200" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-400">Monto (MXN) *</Label>
                <Input type="number" step="0.01" value={nomForm.monto}
                  onChange={e => setNomForm({ ...nomForm, monto: e.target.value })}
                  placeholder="0.00"
                  className="bg-slate-950 border-slate-700 text-slate-200 font-mono"
                  data-testid="nom-monto-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-400">Concepto</Label>
              <Input value={nomForm.concepto}
                onChange={e => setNomForm({ ...nomForm, concepto: e.target.value })}
                placeholder="Ej: Pago semanal"
                className="bg-slate-950 border-slate-700 text-slate-200" />
            </div>

            {/* Calculo automatico por comision */}
            <div className="border-t border-slate-800 pt-3">
              <p className="text-[10px] uppercase tracking-wider text-cyan-400 mb-2">Calcular pago automáticamente</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Desde</Label>
                  <Input type="date" value={nomForm.desde}
                    onChange={e => setNomForm({ ...nomForm, desde: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                    data-testid="nom-desde-input" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-slate-500">Hasta</Label>
                  <Input type="date" value={nomForm.hasta}
                    onChange={e => setNomForm({ ...nomForm, hasta: e.target.value })}
                    className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
                    data-testid="nom-hasta-input" />
                </div>
              </div>
              <Button type="button" size="sm" onClick={calcularSugerido} disabled={calculandoSug}
                className="w-full bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 border border-cyan-800 rounded-sm"
                data-testid="calcular-sugerido-btn">
                {calculandoSug ? 'Calculando...' : 'Calcular sugerido según turnos'}
              </Button>
              {sugerencia && (
                <div className="mt-2 p-2.5 rounded-sm border border-cyan-800 bg-cyan-500/5" data-testid="sugerencia-desglose">
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                    Tipo: <span className="text-cyan-400 font-bold">{sugerencia.tipo_pago}</span>
                    {' · '}
                    <span className="text-slate-400">{sugerencia.turnos_count} carros lavados · ${(sugerencia.total_facturado || 0).toLocaleString()} facturados</span>
                  </p>
                  {(sugerencia.detalle || []).map((d, i) => (
                    <div key={i} className="flex justify-between text-xs">
                      <span className="text-slate-400">{d.label}</span>
                      <span className="text-slate-200 font-mono">${d.monto.toLocaleString()}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-sm border-t border-slate-800 mt-1.5 pt-1.5">
                    <span className="text-slate-300 font-bold">Total sugerido</span>
                    <span className="text-cyan-400 font-mono font-bold">${(sugerencia.monto_sugerido || 0).toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNomDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={guardarPago} disabled={guardando}
              className="bg-cyan-500 hover:bg-cyan-600 text-black font-bold"
              data-testid="guardar-nom-btn">
              {guardando ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
